import React from "react";
import FormInput from "Common/Custom/FormInput";
import FormInputFile from "Common/Custom/FormInputFile";
import FormSubmitButton from "Common/Custom/FormSubmitButton";
import PageBody from "Common/Custom/PageBody";
import { HeadingOne } from "Common/Custom/HeadingOne";
import FormSelect from "Common/Custom/FormSelect";
import FormTextarea from "Common/Custom/FormTextarea";
import FormMultiSelect from "Common/Custom/FormMultiSelect";
import Switch from "Common/Custom/Switch";

/**
 * Shared form for Add Blog and Edit Blog.
 * All data fetching happens in the parent — this component is purely presentational.
 * Supports thumbnail upload via FormInputFile (same pattern as UserAdd).
 */
const BlogForm = ({
    fields = [],
    values,
    errors,
    handleChange,
    handleFileChange,
    handleSubmit,
    loading,
    categoryOptions = [],
    tagOptions = [],
    title = "Blog",
    submitLabel = "Save Blog",
}) => {
    return (
        <PageBody
            headerchildren={<HeadingOne>{title}</HeadingOne>    }
            onSubmit={handleSubmit}
        >
            {fields.map((field) => {

                // ── File upload (thumbnail) ──────────────────────────────────
                if (field.type === "file") {
                    return (
                        <FormInputFile
                            key={field.name}
                            field={field}
                            handleChange={handleFileChange}
                            error={errors[field.name]}
                            imagePreview={values[`${field.name}Preview`] || values[field.name]}
                        />
                    );
                }

                // ── Category select ──────────────────────────────────────────
                if (field.type === "select") {
                    let options = [];
                    if (field.name === "category_id") {
                        options = categoryOptions.map((opt) => ({ value: opt.id, label: opt.name }));
                    }
                    return (
                        <FormSelect
                            key={field.name}
                            field={field}
                            value={values[field.name]}
                            handleChange={handleChange}
                            options={options}
                            error={errors[field.name]}
                        />
                    );
                }

                // ── Tags multi-select ────────────────────────────────────────
                if (field.type === "multiselect") {
                    const options = tagOptions.map((opt) => ({ value: opt.id, label: opt.name }));
                    return (
                        <FormMultiSelect
                            key={field.name}
                            field={field}
                            value={values[field.name] || []}
                            handleChange={handleChange}
                            options={options}
                            placeholder={field.placeholder || "Select tags"}
                            error={errors[field.name]}
                        />
                    );
                }

                // ── Content rich text (Tiptap) ────────────────────────────────
                if (field.type === "textarea" && field.name === "content") {
                    const BlogTiptapEditor = require("../../Components/BlogTiptapEditor").default;

                    return (
                        <div key={field.name} className={field.fieldSize || "col-12"}>
                            <BlogTiptapEditor
                                valueHtml={values[field.name]}
                                onChangeHtml={(html) =>
                                    handleChange({
                                        target: { name: field.name, value: html },
                                    })
                                }
                            />
                            {errors[field.name] && (
                                <div className="text-danger mt-2">{errors[field.name]}</div>
                            )}
                        </div>
                    );
                }

                // ── Textarea ─────────────────────────────────────────────────
                if (field.type === "textarea") {
                    return (
                        <FormTextarea
                            key={field.name}
                            field={field}
                            value={values[field.name]}
                            handleChange={handleChange}
                            error={errors[field.name]}
                        />
                    );
                }


                // ── Switch (publish status) ──────────────────────────────────
                if (field.type === "switch") {
                    return (
                        <Switch
                            key={field.name}
                            values={values[field.name]}
                            handleChange={handleChange}
                            field={field}
                            ValueOne="1"
                            ValueTwo="0"
                            LabelOne="Published"
                            LabelTwo="Draft"
                        />
                    );
                }

                // ── Default text input ───────────────────────────────────────
                return (
                    <FormInput
                        key={field.name}
                        field={field}
                        value={values[field.name]}
                        handleChange={handleChange}
                        error={errors[field.name]}
                    />
                );
            })}

            <FormSubmitButton
                label={submitLabel}
                loading={loading}
                colSize="col-12"
                align="end"
            />
        </PageBody>
    );
};

export default BlogForm;
